import { useState, useEffect, useContext } from 'react'
import styles from './styles.module.css'
import { Input, Select } from '../../../components/ModalInputs'
import Button from '../../../components/Button'
import Table from '../../../components/Table'
import { StaffRosterContext } from '../../../utils/context'
import { useSelector } from 'react-redux'

const API_TAB = 'penalties'

// Данные для дропдаунов
const punishmentTypes = [
  { name: '', value: '' },
  { name: 'Замечание', value: 'remark' },
  { name: 'Выговор', value: 'reprimand' },
  { name: 'Строгий выговор', value: 'severe_reprimand' },
  { name: 'Предупреждение о неполном служебном соответствии', value: 'warning' },
  { name: 'Увольнение', value: 'dismissal' },
  { name: 'Лишение премии', value: 'bonus_deprivation' },
  { name: 'Понижение в должности', value: 'demotion' }
]

const punishmentReasons = [
  { name: '', value: '' },
  { name: 'Нарушение трудовой дисциплины', value: 'discipline' },
  { name: 'Невыполнение должностных обязанностей', value: 'duties' },
  { name: 'Прогул', value: 'absence' },
  { name: 'Появление на работе в состоянии опьянения', value: 'intoxication' },
  { name: 'Нарушение техники безопасности', value: 'safety' },
  { name: 'Разглашение конфиденциальной информации', value: 'confidential' },
  { name: 'Порча имущества', value: 'damage' },
  { name: 'Опоздание на работу', value: 'late' }
]

const punishmentBases = [
  { name: '', value: '' },
  { name: 'Приказ директора', value: 'director_order' },
  { name: 'Акт о нарушении', value: 'violation_act' },
  { name: 'Докладная записка', value: 'memo' },
  { name: 'Решение комиссии', value: 'commission' },
  { name: 'Заключение служебной проверки', value: 'investigation' }
]

const punishmentStatuses = [
  { name: '', value: '' },
  { name: 'Действует', value: 'active' },
  { name: 'Снято досрочно', value: 'removed_early' },
  { name: 'Истек срок действия', value: 'expired' },
  { name: 'Обжаловано', value: 'appealed' }
]

export default function Punish() {
  const { allForms, trackDeletion } = useContext(StaffRosterContext)
  const { values, setFieldValue } = allForms

  // Данные из API (Redux)
  const { personTabData } = useSelector(state => state.personnel)
  const { punish: apiPunish } = personTabData || {}

  const [formData, setFormData] = useState({
    date: '',
    punishmentType: '',
    reason: '',
    documentNumber: '',
    documentDate: '',
    basis: '',
    status: '',
    note: ''
  })

  const [selectedRow, setSelectedRow] = useState(null)

  // Данные таблицы берем из Formik
  const tableData = values.punish || []

  // Синхронизация API данных с Formik (только при первой загрузке)
  useEffect(() => {
    if (apiPunish && !tableData.length) {
      setFieldValue('punish', apiPunish)
    }
  }, [apiPunish, tableData.length, setFieldValue])

  const handleFormChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleRowSelect = (id) => {
    setSelectedRow(selectedRow === id ? null : id)
  }

  const handleAddRow = () => {
    const newRow = {
      id: `new_${Date.now()}`,
      penalty_date: formData.date,
      penalty_type: formData.punishmentType,
      reason: formData.reason,
      document_number: formData.documentNumber,
      document_date: formData.documentDate,
      basis: formData.basis,
      status: formData.status,
      note: formData.note
    }
    // Сохраняем в Formik
    setFieldValue('punish', [...tableData, newRow])
  }

  const handleDeleteRow = () => {
    if (selectedRow !== null) {
      const rowToDelete = tableData.find(row => (row.id || row.xp_key) === selectedRow)
      if (rowToDelete) {
        const pk = rowToDelete.xp_key || rowToDelete.id || rowToDelete.xp_fm_id
        if (pk && !String(pk).startsWith('new_')) {
          trackDeletion(API_TAB, pk)
        }
      }
      // Обновляем в Formik
      const updatedData = tableData.filter(row => (row.id || row.xp_key) !== selectedRow)
      setFieldValue('punish', updatedData)
      setSelectedRow(null)
    }
  }

  const renderOptions = ({ name, value }) => (
    <option key={value || 'empty'} value={value}>{name}</option>
  )

  return (
    <div className={styles.tabWrapper}>
      {/* Форма */}
      <div className={styles.qualificationForm}>
        {/* Строка 1 */}
        <div className={styles.inputsRow}>
          <Input
            label="Дата взыскания"
            type="date"
            name="date"
            value={formData.date}
            onChange={handleFormChange}
          />
          <Select
            label="Вид взыскания"
            name="punishmentType"
            value={formData.punishmentType}
            onChange={handleFormChange}
          >
            {punishmentTypes.map(renderOptions)}
          </Select>
        </div>

        {/* Строка 2 */}
        <div className={styles.inputsRow}>
          <Select
            label="Причина взыскания"
            name="reason"
            value={formData.reason}
            onChange={handleFormChange}
          >
            {punishmentReasons.map(renderOptions)}
          </Select>
          <Select
            label="Статус"
            name="status"
            value={formData.status}
            onChange={handleFormChange}
          >
            {punishmentStatuses.map(renderOptions)}
          </Select>
        </div>

        {/* Строка 3 */}
        <div className={styles.inputsRow}>
          <Input
            label="Номер документа"
            name="documentNumber"
            value={formData.documentNumber}
            onChange={handleFormChange}
          />
          <Input
            label="Дата документа"
            type="date"
            name="documentDate"
            value={formData.documentDate}
            onChange={handleFormChange}
          />
        </div>

        {/* Строка 4 */}
        <div className={styles.inputsRow}>
          <Select
            label="Основание"
            name="basis"
            value={formData.basis}
            onChange={handleFormChange}
          >
            {punishmentBases.map(renderOptions)}
          </Select>
        </div>

        {/* Строка 5 */}
        <div className={styles.inputsRow}>
          <Input
            label="Примечание"
            name="note"
            value={formData.note}
            onChange={handleFormChange}
          />
          <div className={styles.qualificationButtonsInline}>
            <Button onClick={handleAddRow}>+ Добавить строку</Button>
            <Button mode="delete" onClick={handleDeleteRow}>× Удалить строку</Button>
          </div>
        </div>
      </div>

      {/* Таблица */}
      <div className={styles.qualificationTableSection}>
        <Table>
          <thead>
            <tr>
              <th>Дата взыскания</th>
              <th>Вид взыскания</th>
              <th>Причина</th>
              <th>Номер документа</th>
              <th>Дата документа</th>
              <th>Основание</th>
              <th>Статус</th>
            </tr>
          </thead>
          <tbody>
            {tableData.length > 0 ? (
              tableData.map(row => {
                const rowId = row.id || row.xp_key
                return (
                  <tr
                    key={rowId}
                    onClick={() => handleRowSelect(rowId)}
                    className={selectedRow === rowId ? styles.selectedRow : ''}
                  >
                    <td>{row.penalty_date || row.date}</td>
                    <td>{row.penalty_type || row.punishmentType}</td>
                    <td>{row.reason}</td>
                    <td>{row.document_number || row.documentNumber}</td>
                    <td>{row.document_date || row.documentDate}</td>
                    <td>{row.basis}</td>
                    <td>{row.status}</td>
                  </tr>
                )
              })
            ) : (
              <tr>
                <td colSpan="7" className={styles.emptyTable}>
                  Нет данных о взысканиях
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  )
}
